import React, { useState, useMemo } from "react";
import { NavigateFunction } from "react-router-dom";
import { IconChevronLeft, IconChevronRight } from "@tabler/icons-react";
import { Button, Typography } from "@altimateai/lego";
import { Stack } from "@altimateai/lego";
import { EvalResult } from "../../services/evals";
import { RunContainer } from "./RunContainer";

interface PaginatedResultsViewProps {
  evalResults: EvalResult[];
  expandedResultId: string | null;
  setExpandedResultId: (id: string | null) => void;
  deleteResultMutation: {
    mutate: (id: string) => void;
  };
  navigate: NavigateFunction;
}

const RUNS_PER_PAGE = 10;

export const PaginatedResultsView: React.FC<PaginatedResultsViewProps> = ({
  evalResults,
  expandedResultId,
  setExpandedResultId,
  deleteResultMutation,
  navigate,
}) => {
  const [currentPage, setCurrentPage] = useState(1);

  // Group results by run_name
  const groupedResults = useMemo(() => {
    const groups: Record<string, EvalResult[]> = {};

    evalResults.forEach(result => {
      const runName = result.run_name || "Ungrouped";
      if (!groups[runName]) {
        groups[runName] = [];
      }
      groups[runName].push(result);
    });

    // Sort results within each group by created_at (newest first)
    Object.keys(groups).forEach(runName => {
      groups[runName].sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
    });

    return groups;
  }, [evalResults]);

  // Get run names sorted by most recent result in each run
  const runNames = useMemo(() => {
    return Object.keys(groupedResults).sort((a, b) => {
      const latestA = groupedResults[a][0]?.created_at || "";
      const latestB = groupedResults[b][0]?.created_at || "";
      return new Date(latestB).getTime() - new Date(latestA).getTime();
    });
  }, [groupedResults]);

  // Calculate pagination by runs
  const totalPages = Math.ceil(runNames.length / RUNS_PER_PAGE);
  const startIndex = (currentPage - 1) * RUNS_PER_PAGE;
  const endIndex = startIndex + RUNS_PER_PAGE;
  const paginatedRunNames = runNames.slice(startIndex, endIndex);

  // Handle pagination
  const handlePrevPage = () => {
    setCurrentPage(prev => Math.max(1, prev - 1));
  };

  const handleNextPage = () => {
    setCurrentPage(prev => Math.min(totalPages, prev + 1));
  };

  if (runNames.length === 0) {
    return (
      <div className="al-text-center al-py-8">
        <Typography className="al-text-gray-500">No results found</Typography>
      </div>
    );
  }

  return (
    <div className="al-space-y-4">
      {/* Stats Header */}
      <Stack direction="row" justify="between" align="center">
        <Typography size="sm" className="al-text-gray-600">
          Total: {runNames.length} {runNames.length === 1 ? "run" : "runs"}, {evalResults.length}{" "}
          {evalResults.length === 1 ? "result" : "results"}
        </Typography>

        {totalPages > 1 && (
          <Typography size="sm" className="al-text-gray-600">
            Showing {startIndex + 1}-{Math.min(endIndex, runNames.length)} of {runNames.length} runs
          </Typography>
        )}
      </Stack>

      {/* Run Containers */}
      <div className="al-space-y-4">
        {paginatedRunNames.map(runName => (
          <RunContainer
            key={runName}
            runName={runName}
            results={groupedResults[runName]}
            expandedResultId={expandedResultId}
            setExpandedResultId={setExpandedResultId}
            deleteResultMutation={deleteResultMutation}
            navigate={navigate}
          />
        ))}
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <Stack direction="row" justify="between" align="center" className="al-pt-2">
          <Button onClick={handlePrevPage} disabled={currentPage === 1} variant="outline" size="sm">
            <IconChevronLeft className="al-w-4 al-h-4 al-mr-1" />
            Previous
          </Button>

          <Typography size="sm" className="al-text-gray-600">
            Page {currentPage} of {totalPages}
          </Typography>

          <Button
            onClick={handleNextPage}
            disabled={currentPage === totalPages}
            variant="outline"
            size="sm"
          >
            Next
            <IconChevronRight className="al-w-4 al-h-4 al-ml-1" />
          </Button>
        </Stack>
      )}
    </div>
  );
};
